import React, { useState, useContext, useEffect, Dispatch, SetStateAction } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import { Input, Checkbox, Button, Space, Tooltip, Typography, notification, InputNumber } from 'antd'
import { DeleteOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons'
import { BiImageAdd as AddImageIcon } from "react-icons/bi"

import { Course, Question } from '../Course/types'
import { CourseContext } from '../Course/CourseContext'
import { FlexColumn, FlexRow } from '../FlexboxReusables/FlexItems'
import { View } from '../../Routes/Course'
import { ImageUploadModal } from './ImageUploadModal'
import { imageUrl } from '../../globals'

type QuestionsProps = {
  course: Course
  setSelectedView?: Dispatch<SetStateAction<View>>
  isReviewMode?: boolean
}

type ModalContext = {
  show: boolean
  imageType: 'question' | 'answer'
  questionId: number
  answerId: number | null
}

const Questions: React.FC<QuestionsProps> = ({ course, isReviewMode }) => {
  const [questions, setQuestions] = useState<Array<Question>>(course.questions || [])
  const [currentQuestion, setCurrentQuestion] = useState<string>('')
  const [currentQuestionWeight, setCurrentQuestionWeight] = useState<string>('1') // input gets parsed in 'createQuestion'
  const [totalPoints, setTotalPoints] = useState<number>(course.totalPoints || 0)
  const [minPercentageToPass, setMinPercentageToPass] = useState<number>(course.minPercentageToPass || 0)
  const [questionPoolSize, setQuestionPoolSize] = useState<number>(course.questionPoolSize || questions.length)
  const [modalContext, setModalContext] = useState<ModalContext>({ show: false, imageType: 'question', questionId: 0, answerId: null })

  const intl = useIntl()
  const { updateCourse, saveCourse } = useContext(CourseContext)

  useEffect(() => {
    updateCourse({
      ...course,
      hasUnsavedChanges: true,
      minPercentageToPass,
      totalPoints,
      questionPoolSize,
    })
  }, [questions, minPercentageToPass, totalPoints, questionPoolSize])


  const createQuestion = () => {
    const currentQuestionWeightInt = parseInt(currentQuestionWeight)

    if (!currentQuestionWeight) {
      notification.open({
        message: intl.formatMessage({ id: 'generic.validationError' }),
        description: intl.formatMessage({ id: 'editor.questions.validationErrorQuestionWeight' }),
        type: 'error',
        icon: <WarningOutlined style={{ color: 'red' }} />,
        placement: 'bottomRight',
      })

      return
    }

    if (currentQuestion) {
      setQuestions(
        [
          ...questions,
          {
            text: currentQuestion,
            answers: [],
            questionWeight: currentQuestionWeightInt
          }
        ])

      setTotalPoints(totalPoints + currentQuestionWeightInt)
      setCurrentQuestionWeight('1')
      setCurrentQuestion('')
    }
  }

  const addQuestionsToCourse = () => {
    const isValid = validateQuestions(questions)

    if (!isValid) {
      notification.open({
        message: intl.formatMessage({ id: 'generic.validationError' }),
        description: intl.formatMessage({ id: 'editor.questions.validationErrorAnswers' }),
        type: 'error',
        icon: <WarningOutlined style={{ color: 'orange' }} />,
        placement: 'bottomRight',
      })

      return
    }
    const newCourse = { ...course }
    newCourse.questions = questions

    updateCourse(newCourse)

    if (isReviewMode) {
      saveCourse(newCourse, intl) // in review mode, the user has no access to the regular "Save" button so we need to save the course here
    }

    if (!isReviewMode) {
      notification.open({
        message: intl.formatMessage({ id: 'notification.success' }),
        description: intl.formatMessage({ id: 'notification.success.questionsAdded' }),
        type: 'success',
        placement: 'bottomRight',
      })
    }
  }

  const validateQuestions = (questions: Array<Question>): boolean => {
    for (const question of questions) {
      const { answers } = question

      const hasAtLeastOneCorrectAnswer = answers.some((answer) => answer.isCorrect)
      const hasEmptyAnswers = answers.some((answer) => !answer.text.trim())
      const hasLessThanTwoAnswers = answers.length < 2

      if (!hasAtLeastOneCorrectAnswer || hasEmptyAnswers || hasLessThanTwoAnswers) {
        return false
      }
    }

    return true
  }

  const deleteQuestion = (questionIndex: number) => {
    if (confirm(intl.formatMessage({ id: 'editor.questions.confirmDelete' }))) {
      const newQuestions = [...questions]
      newQuestions.splice(questionIndex, 1)

      setTotalPoints(totalPoints - questions[questionIndex].questionWeight)
      setQuestions(newQuestions)
    }
  }

  const handleAnswerInputChange = (questionIndex: number, answerIndex: number, event: string) => {
    const newQuestions = [...questions]
    newQuestions[questionIndex].answers[answerIndex].text = event

    setQuestions(newQuestions)
  }

  const handleValidAnswerCheckboxChange = (questionIndex: number, answerIndex: number) => {
    const newQuestions = [...questions]
    newQuestions[questionIndex].answers[answerIndex].isCorrect = !newQuestions[questionIndex].answers[answerIndex].isCorrect

    setQuestions(newQuestions)
  }

  // made async to be able to rapidly add answers by pressing Enter
  // (next input must be present in DOM before it can be focused)
  const addAnswer = async (questionIndex: number) => {
    const newQuestions = [...questions]
    newQuestions[questionIndex].answers.push({ text: '', isCorrect: false })

    setQuestions(newQuestions)
  }

  const handleKeyDown = (questionIndex: number, answerIndex: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      addAnswer(questionIndex).then(() => {
        const nextAnswerInput = document.getElementById(`answer-${questionIndex}-${answerIndex + 1}`)

        if (nextAnswerInput) {
          nextAnswerInput.focus()
        }
      })
    }
  }

  const deleteAnswer = (questionIndex: number, answerIndex: number) => {
    const newQuestions = [...questions]
    newQuestions[questionIndex].answers.splice(answerIndex, 1)
    setQuestions(newQuestions)
  }

  return (
    <>
      <QuestionsContainer>
        <FlexRow justifyContent='center'>
          <Typography.Title level={3}>
            <FormattedMessage id='editor.questions.heading' />
          </Typography.Title>
        </FlexRow>

        <FlexRow justifyContent='space-evenly'>
          <FlexColumn margin='1em' height='6em' justifyContent='space-evenly'>
            <Typography.Text strong>
              <FormattedMessage id='editor.questions.totalPoints' />
            </Typography.Text>
            <InputNumber
              min={1}
              max={1000}
              onChange={(e) => setTotalPoints(e || 0)}
              value={totalPoints || 0}
              style={{ width: '5em' }}
            />
          </FlexColumn>

          <FlexColumn margin='1em' height='6em' justifyContent='space-evenly'>
            <Tooltip title={<FormattedMessage id='editor.questions.questionPoolSizeTooltip' />}>
              <Typography.Text strong>
                <FormattedMessage id='editor.questions.questionPoolSize' />
              </Typography.Text>
            </Tooltip>
            <InputNumber
              min={1}
              max={questions.length}
              onChange={(e: number | null) => setQuestionPoolSize((e || questions.length))}
              value={questionPoolSize || questions.length}
              style={{ width: '5em' }}
            />
          </FlexColumn>

          <FlexColumn margin='1em' height='6em' justifyContent='space-evenly'>
            <Typography.Text strong>
              <FormattedMessage id='editor.questions.minimumTargetAchievement' />
            </Typography.Text>
            <InputNumber
              addonAfter='%'
              min={1}
              max={100}
              onChange={(e) => setMinPercentageToPass(e || 0)}
              value={minPercentageToPass}
              style={{ width: '25%' }}
            />
            ({Math.round((minPercentageToPass / 100) * totalPoints)} <FormattedMessage id='generic.points' />)
          </FlexColumn>
        </FlexRow>

        {questions.map((question, questionIndex) => (
          <>
            <FlexRow key={questionIndex} justifyContent='center' >
              <FlexColumn
                key={questionIndex}
                alignItems='flex-start'
                padding='2em'
                width='50%'
              >
                <FlexRow justifyContent='flex-start'>
                  <FlexRow justifyContent='flex-start'>
                    <BoldText style={{ minWidth: '5em' }}>
                      <FormattedMessage id='generic.question' /> {`${questionIndex + 1}`}:
                    </BoldText>
                    <QuestionText>
                      {question.text}
                    </QuestionText>
                    {/* TODO: i18n */}
                    <Tooltip
                      title={'Bild zur Frage hinzufügen'}
                      mouseEnterDelay={1}
                    >
                      <Button onClick={() => setModalContext({ show: true, imageType: 'question', questionId: question.id || 0, answerId: null })}>
                        <AddImageIcon size={20} style={{ marginTop: '0.1em' }} />
                      </Button>
                    </Tooltip>
                    <Tooltip title={<FormattedMessage id='editor.questions.deleteQuestion' />}>
                      <Button onClick={() => deleteQuestion(questionIndex)}>
                        <DeleteOutlined style={{ color: 'red' }} />
                      </Button>
                    </Tooltip>
                  </FlexRow>
                  <FlexRow justifyContent='flex-end' margin='0 5em'>
                    {question.questionWeight}{" "}
                    {question.questionWeight === 1
                      ? <FormattedMessage id='generic.point' />
                      : <FormattedMessage id='generic.points' />
                    }
                  </FlexRow>
                </FlexRow>
                <FlexRow justifyContent='center'>
                  <QuestionAnswerImage
                    src={`${imageUrl}/${course.id}/questions/${question.id}/question.jpg`}
                    alt=''
                    onClick={() => window.open(`${imageUrl}/${course.id}/questions/${question.id}/question.jpg`, '_blank')}
                  />
                </FlexRow>

                {question.answers.map((answer, answerIndex) => (
                  <>
                    <FlexRow key={answerIndex} width='100%' height='3em' margin='0 0 0 2em'>
                      <Tooltip
                        title={<FormattedMessage id='editor.questions.validAnswersMustBeChecked' />}
                        mouseEnterDelay={1}
                      >
                        <Checkbox
                          checked={answer.isCorrect}
                          onChange={() => handleValidAnswerCheckboxChange(questionIndex, answerIndex)}
                        />
                      </Tooltip>
                      <Input
                        id={`answer-${questionIndex}-${answerIndex}`}
                        value={answer.text}
                        onChange={(event) => handleAnswerInputChange(questionIndex, answerIndex, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(questionIndex, answerIndex, event)}
                        placeholder={intl.formatMessage({ id: 'editor.questions.answers.answerPlaceholder' })}
                        style={{ margin: '1em' }}
                      />
                      <Tooltip
                        title={'Bild zur Antwort hinzufügen'}
                        mouseEnterDelay={1}
                      >
                        <Button onClick={() => setModalContext({ show: true, imageType: 'answer', questionId: question.id || 0, answerId: answer.id || null })}>
                          {/* TODO: i18n */}
                          <AddImageIcon size={20} style={{ marginTop: '0.1em' }} />
                        </Button>
                      </Tooltip>
                      <Tooltip
                        title={<FormattedMessage id='editor.questions.answers.deleteAnswer' />}
                        mouseEnterDelay={1}
                      >
                        <Button onClick={() => deleteAnswer(questionIndex, answerIndex)}>
                          <DeleteOutlined style={{ color: 'red' }} />
                        </Button>
                      </Tooltip>
                    </FlexRow>
                    <FlexRow justifyContent='center'>
                      <QuestionAnswerImage
                        src={`${imageUrl}/${course.id}/questions/${question.id}/answers/${answer.id}/answer.jpg`}
                        alt=''
                        onClick={() => window.open(`${imageUrl}/${course.id}/questions/${question.id}/answers/${answer.id}/answer.jpg`, '_blank')}
                      />
                    </FlexRow>
                  </>
                ))}
                <Space>
                  <FlexRow margin='1em'>
                    <Button onClick={() => addAnswer(questionIndex)}>
                      <FormattedMessage id='editor.questions.answers.addAnswer' />
                    </Button>
                  </FlexRow>
                </Space>
              </FlexColumn>
            </FlexRow>
            <ImageUploadModal
              showImageUploadModal={modalContext.show}
              setShowImageUploadModal={(show) => setModalContext(prev => ({ ...prev, show }))}
              courseId={course.id}
              questionId={modalContext.questionId}
              answerId={modalContext.answerId}
              imageType={modalContext.imageType}
            />
          </>
        ))}
        <FlexColumn margin='2em'>
          <FlexRow padding='0 1em' justifyContent='center'>
            <Typography.Title level={5}>
              <FormattedMessage id="editor.questions.createQuestion" />
            </Typography.Title>
          </FlexRow>
          <FlexRow margin='0.5em' width='50%' alignSelf='center'>
            <Input
              value={currentQuestion}
              onChange={(event) => setCurrentQuestion(event.target.value)}
              placeholder={intl.formatMessage({ id: 'editor.questions.createQuestion' })}
            />
            <Tooltip
              title={<FormattedMessage id='editor.questions.questionWeight' />}
              mouseEnterDelay={1}
            >
              <Input
                value={currentQuestionWeight}
                onChange={(event) => setCurrentQuestionWeight(event.target.value)}
                placeholder={intl.formatMessage({ id: 'editor.questions.pointsPlaceholderAbbreviated' })}
                style={{ width: '5em', margin: '0 1em 0 1em' }}
              />
            </Tooltip>
            <Button
              type='primary'
              onClick={createQuestion}
              style={{ fontWeight: 'bold' }}
            >
              +
            </Button>
          </FlexRow>
        </FlexColumn>
        <FlexRow padding='1em' justifyContent='center'>
          <Button type='primary' onClick={addQuestionsToCourse} disabled={!questions.length}>
            {isReviewMode
              ? (<FormattedMessage id='generic.update' />)
              : (<FormattedMessage id='editor.questions.addQuestionsAndAnswersToCourse' />)
            }
            <CheckCircleOutlined style={{ color: questions.length ? 'green' : 'grey', marginLeft: '0.5em' }} />
          </Button>
        </FlexRow>
      </QuestionsContainer>
    </>
  )
}

export default Questions

const QuestionText = styled.div`
  margin-right: 1em;

`
const QuestionsContainer = styled.div`
  margin-top: 0.5em;
  padding-bottom: 2em;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const BoldText = styled(Typography.Text)`
  font-weight: bold;
  margin-right: 0.5em;
`

const QuestionAnswerImage = styled.img`
  width: 50%;
  margin: 1em 0;
  height: auto;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.05);
    border-color: lightgrey;
  }
`